import { getApiBaseUrl } from '../lib/getApiUrl';
import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ExportConfig, ExportJob } from '@/types/import';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

export interface UseLeadExportReturn {
  startExport: (config: ExportConfig) => Promise<{ jobId: string }>;
  downloadExport: (jobId: string) => void;
  getJobStatus: (jobId: string) => Promise<ExportJob>;
  exportJobs: ExportJob[];
  isExporting: boolean;
  error: string | null;
  clearError: () => void;
}

export const useLeadExport = (): UseLeadExportReturn => {
  const queryClient = useQueryClient();
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch export jobs history
  const { data: exportJobs = [] } = useQuery<ExportJob[]>({
    queryKey: ['exportJobs'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/leads/export/jobs`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        // Return empty array if endpoint doesn't exist yet
        if (response.status === 404) {
          return [];
        }
        throw new Error('Failed to fetch export jobs');
      }
      return response.json();
    },
    staleTime: 30000,
  });

  // Start export process
  const startExport = useCallback(async (config: ExportConfig): Promise<{ jobId: string }> => {
    setIsExporting(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/leads/export`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(config),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to start export');
      }

      const result = await response.json();
      queryClient.invalidateQueries({ queryKey: ['exportJobs'] });
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start export';
      setError(message);
      throw err;
    } finally {
      setIsExporting(false);
    }
  }, [queryClient]);

  // Download exported file
  const downloadExport = useCallback((jobId: string) => {
    const token = localStorage.getItem('authToken');
    const url = `${API_BASE_URL}/leads/export/jobs/${jobId}/download`;

    // Create a temporary link to trigger download
    const link = document.createElement('a');
    link.href = `${url}?token=${token}`;
    link.setAttribute('download', '');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  // Get job status
  const getJobStatus = useCallback(async (jobId: string): Promise<ExportJob> => {
    const response = await fetch(`${API_BASE_URL}/leads/export/jobs/${jobId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch job status');
    }

    return response.json();
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    startExport,
    downloadExport,
    getJobStatus,
    exportJobs,
    isExporting,
    error,
    clearError,
  };
};
